<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import '@vueup/vue-quill/dist/vue-quill.snow.css'
import { QuillEditor } from '@vueup/vue-quill'
import { useRoute, useRouter } from 'vue-router'
import PageContent from '@/components/page-content.vue'
import ContentLoader from '@/components/content-loader.vue'
import HeroSplashError from '@/components/hero-splash-error.vue'
import useLoader from '@/composables/loader'
import ButtonPrimary from '@/components/button-primary.vue'
import ButtonHighlighted from '@/components/button-highlighted.vue'
import ButtonSecondary from '@/components/button-secondary.vue'
import { faFloppyDisk, faHandPointUp, faXmark, faArrowLeft } from '@fortawesome/pro-regular-svg-icons'
import CreditNoteSkeleton from '@/features/credit-notes/components/CreditNoteSkeleton.vue'
import InvoiceSkeletonOwnerDetails from '@/features/invoices/components/partials/InvoiceSkeletonOwnerDetails.vue'
import CreditNoteSkeletonCustomerDetails from '@/features/credit-notes/components/CreditNoteSkeletonCustomerDetails.vue'
import CreditNoteSkeletonBillTotal from '@/features/credit-notes/components/CreditNoteBillTotal.vue'
import CreditNoteSkeletonDetails from '@/features/credit-notes/components/CreditNoteSkeletonDetails.vue'
import Card from '@/components/Card/card-container.vue'
import CardContent from '@/components/Card/card-content.vue'
import CardHeader from '@/components/Card/card-header.vue'
import CreditNoteLines from '@/features/credit-notes/components/CreditNoteLines.vue'
import { CreditNoteStatus } from '@/features/credit-notes/constants'
import { calcCreditNoteTotals } from '@/features/credit-notes/utils/totals'
import {
  useGetCreditNote,
  useUpdateCreditNoteLines,
  useDeleteCreditNoteLine,
  useApproveCreditNote,
  useSendCreditNote,
  useCancelCreditNote
} from '@/composables/billing/credit-notes'
import PageTitle from '@/components/page-title.vue'
import useNotifications from '@/composables/notifications'
import { type CreditNoteCharge, type CreditNote } from '@/api/billing/credit-note-schemas'

defineOptions({ name: 'EditCreditNote' })

const route = useRoute()
const router = useRouter()
const { addSuccess, addError } = useNotifications()
const { wrap } = useLoader()

const creditNoteId = computed(() => {
  const n = Number(route.params.id)
  return Number.isFinite(n) ? n : 0
})

const { data: note, isLoading: creditNoteLoading, isError } = useGetCreditNote(creditNoteId)
const { mutateAsync: updateLines, isPending: isSaving } = useUpdateCreditNoteLines(creditNoteId)
const { mutateAsync: deleteLine, isPending: isDeletingLine } = useDeleteCreditNoteLine(creditNoteId)
const { mutateAsync: approve, isPending: isApproving } = useApproveCreditNote()
const { mutateAsync: send } = useSendCreditNote()
const { mutateAsync: cancel, isPending: isCancelling } = useCancelCreditNote()

type Line = { id?: number; description: string; kWh: number; tariffPerKWh: number }
const lines = ref<Line[]>([])
const vatPercentage = ref(0)
const notes = ref('')

function goBackToDashboard() {
  router.push({ name: 'invoices.list' })
}

function normaliseVat(value: unknown): number {
  if (typeof value !== 'number') return 0
  if (Number.isNaN(value)) return 0
  if (value <= 0 || value > 100) return 0
  return value
}

function normaliseNotes(value: unknown): string {
  const s = String(value ?? '').trim()
  return s === '<p><br></p>' ? '' : s
}

function lineSignature(l: { id?: number; description?: string; kWh?: number; tariffPerKWh?: number }) {
  const idPart = l.id != null ? `id:${l.id}` : 'id:none'
  const desc = (l.description ?? '').trim()
  const kwh = Number(l.kWh ?? 0)
  const tariff = Number(l.tariffPerKWh ?? 0)
  return `${idPart}|d:${desc}|k:${kwh}|t:${tariff}`
}

watch(
  note,
  n => {
    if (!n) return
    vatPercentage.value = normaliseVat(n.vatPercentage)
    notes.value = n.notes ?? ''

    const src = n.charges?.length ? n.charges : [{ description: '', kWh: 0, tariffPerKWh: 0 }]
    lines.value = src.map<Line>(l => ({
      id: l.id,
      description: l.description ?? '',
      kWh: l.kWh ?? 0,
      tariffPerKWh: l.tariffPerKWh ?? 0
    }))
  },
  { immediate: true }
)

const hasUnsavedChanges = computed(() => {
  const n = note.value
  if (!n) return false
  if (normaliseVat(n.vatPercentage) !== normaliseVat(vatPercentage.value)) return true
  if (normaliseNotes(n.notes) !== normaliseNotes(notes.value)) return true

  const server = (n.charges ?? []).map(l => lineSignature(l)).sort()
  const local = lines.value.map(l => lineSignature(l)).sort()
  if (server.length !== local.length) return true
  for (let i = 0; i < server.length; i++) {
    if (server[i] !== local[i]) return true
  }
  return false
})

const hasUniqueDescriptions = computed(() => {
  const seen = new Set<string>()
  for (const l of lines.value) {
    const desc = (l.description ?? '').trim()
    if (!desc) continue
    const key = desc.toLowerCase()
    if (seen.has(key)) return false
    seen.add(key)
  }
  return true
})

const isEditableStatus = computed(() => note.value?.status === CreditNoteStatus.Draft) // "ActionRequired" maps to Draft in store
const isStatusApproved = computed(() => note.value?.status === CreditNoteStatus.Approved)
const canDeleteLine = computed(() => isEditableStatus.value && lines.value.length > 1)

const isBusy = computed(
  () =>
    creditNoteLoading.value ||
    isSaving.value ||
    isDeletingLine.value ||
    isApproving.value ||
    isCancelling.value
)

const canSave = computed(() => {
  if (!isEditableStatus.value) return false
  if (!note.value) return false
  if (!hasUnsavedChanges.value) return false
  if (!lines.value.length) return false
  if (!hasUniqueDescriptions.value) return false
  return lines.value.every(l => (l.description ?? '').trim().length > 0)
})

const canApprove = computed(() => isEditableStatus.value && !hasUnsavedChanges.value)
const totals = computed(() => calcCreditNoteTotals(lines.value, vatPercentage.value))
const totalCents = computed(() => totals.value.totalCents)

type CreditNoteWithTotals = CreditNote & { totalInFractionalCurrency?: number }
const noteWithTotals = computed(() => note.value as CreditNoteWithTotals | undefined)

const displayTotalCents = computed(() => {
  const n = noteWithTotals.value
  if (!n) return 0
  return hasUnsavedChanges.value ? totalCents.value : (n.totalInFractionalCurrency ?? totalCents.value)
})

function addLine() {
  if (!isEditableStatus.value) return
  lines.value.push({ description: '', kWh: 0, tariffPerKWh: 0 })
}

async function removeLine(i: number) {
  if (!isEditableStatus.value) return

  await wrap(async () => {
    if (lines.value.length <= 1) return

    const line = lines.value[i]
    if (!line?.id) {
      lines.value.splice(i, 1)
      return
    }

    try {
      await deleteLine(line.id)
      addSuccess({ title: 'Line removed' })
    } catch {
      addError({ title: 'Unable to remove line' })
    }
  })
}

function updateLine({ index, line }: { index: number; line: Line }) {
  if (!isEditableStatus.value) return
  lines.value[index] = line
}

async function doSave() {
  if (!canSave.value) return

  await wrap(async () => {
    const payload: CreditNoteCharge[] = lines.value.map(l => ({
      id: l.id,
      description: l.description,
      kWh: l.kWh,
      tariffPerKWh: l.tariffPerKWh
    }))

    await updateLines({
      charges: payload,
      vatPercentage: vatPercentage.value,
      notes: notes.value
    })

    addSuccess({ title: 'Credit note saved' })
  })
}

async function doApprove() {
  if (!canApprove.value) return

  await wrap(async () => {
    try {
      await approve(creditNoteId.value)
      addSuccess({ title: 'Approved' })
    } catch {
      addError({ title: 'Unable to approve' })
    }
  })
}

async function doApproveAndSend() {
  if (!canApprove.value) return

  await wrap(async () => {
    try {
      await approve(creditNoteId.value)
      await send(creditNoteId.value)
      addSuccess({ title: 'Approved & sent' })
    } catch {
      addError({ title: 'Approve & Send failed' })
    }
  })
}

async function doSend() {
  await wrap(async () => {
    try {
      await send(creditNoteId.value)
      addSuccess({ title: 'Sent' })
    } catch {
      addError({ title: 'Unable to send' })
    }
  })
}

async function doCancel() {
  await wrap(async () => {
    try {
      await cancel(creditNoteId.value)
      router.push({ name: 'invoices.list' })
      addSuccess({ title: 'Credit note cancelled' })
    } catch {
      addError({ title: 'Unable to cancel a credit note' })
    }
  })
}
</script>

<template>
  <PageContent>
    <ContentLoader :loading="creditNoteLoading && !note" />
    <HeroSplashError v-if="isError && !note" />

    <div v-else-if="note">
      <div class="mb-4">
        <PageTitle>
          {{ note?.projectName }}
          <template #subtitle>(Project: {{ note.projectCode }} | Asset: {{ note.assetCode }})</template>
        </PageTitle>

        <div class="mt-2">
          <ButtonSecondary
            :icon="faArrowLeft"
            :class="['one-h-width', $style['back-to-dashboard-btn']]"
            @click="goBackToDashboard">
            Back to Dashboard
          </ButtonSecondary>
        </div>
      </div>

      <CreditNoteSkeleton title="Credit Note">
        <template #credit-note-details>
          <CreditNoteSkeletonDetails
            :credit-note-number="note.creditNoteNumber"
            :date="note.date"
            :date-format="note.creditNoteDateFormat" />
        </template>

        <template #bill-total>
          <CreditNoteSkeletonBillTotal :amount="displayTotalCents" label="Your Credit" />
        </template>

        <template #owner-details>
          <InvoiceSkeletonOwnerDetails
            :logo-url="note.owner.logoUrl ?? null"
            :name="note.owner.name"
            :address="note.owner.address"
            :vat-number="note.owner.vatNumber" />
        </template>

        <template #customer-details>
          <CreditNoteSkeletonCustomerDetails
            :name="note.customer.name"
            :address="[
              note.customer.addressLineOne ?? null,
              note.customer.addressLineTwo ?? null,
              note.customer.suburb ?? null,
              note.customer.city ?? null,
              note.customer.region ?? null,
              note.customer.country ?? null
            ]"
            :vat-number="note.customer.vatNumber ?? null" />
        </template>

        <template #line-items>
          <CreditNoteLines
            :lines="lines"
            :vat-percentage="vatPercentage"
            :can-delete="canDeleteLine"
            :readonly="!isEditableStatus"
            @add="addLine"
            @remove="removeLine"
            @update:line="updateLine" />
        </template>

        <template #notes>
          <Card class="mt-4">
            <CardHeader uppercase>NOTES</CardHeader>
            <CardContent>
              <QuillEditor
                v-model:content="notes"
                :readOnly="!isEditableStatus"
                contentType="html"
                theme="snow"
                placeholder="Please type notes here"
                style="min-height: 120px" />
            </CardContent>
          </Card>
        </template>
      </CreditNoteSkeleton>

      <div class="mt-4 d-flex flex-wrap align-items-center gap-3">
        <ButtonPrimary
          :icon="faFloppyDisk"
          class="color-temp-white"
          :disabled="!canSave || isBusy"
          @click="doSave">
          Save
        </ButtonPrimary>

        <ButtonSecondary :icon="faXmark" class="one-h-width" :disabled="isBusy" @click="doCancel">
          Cancel
        </ButtonSecondary>

        <template v-if="isEditableStatus">
          <div class="vr"></div>

          <ButtonHighlighted :disabled="isBusy || !canApprove" @click="doApprove">Approve</ButtonHighlighted>

          <ButtonSecondary
            outline="teal"
            :icon="faHandPointUp"
            :disabled="isBusy || !canApprove"
            @click="doApproveAndSend">
            Approve &amp; Send
          </ButtonSecondary>
        </template>

        <ButtonHighlighted v-else-if="isStatusApproved" :disabled="isBusy" @click="doSend">Send</ButtonHighlighted>
      </div>
    </div>
  </PageContent>
</template>

<style module>
.back-to-dashboard-btn {
  min-width: 180px;
}
</style>
